import { useEffect } from "react";
import Lenis from "lenis";
import { LanguageContext } from "@/context/LanguageContext";
import { translations, LANGS, DEFAULT_LANG, pageSlugs } from "@/i18n/translations";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppButton, MobileContactBar } from "@/components/ContactWidgets";
import { siteConfig } from "@/config/site";
import { buildHead } from "@/config/seo";

// Upsert a <head> element by selector; creates it with the given attributes if missing.
const upsert = (selector, tag, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = document.createElement(tag); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const applyHead = (head) => {
  document.documentElement.lang = head.lang;
  document.title = head.title;
  upsert('meta[name="description"]', "meta", { name: "description", content: head.description });
  upsert('link[rel="canonical"]', "link", { rel: "canonical", href: head.canonical });
  Object.entries({
    "og:type": head.og.type, "og:site_name": head.og.siteName, "og:locale": head.og.locale,
    "og:title": head.og.title, "og:description": head.og.description, "og:url": head.og.url, "og:image": head.og.image,
  }).forEach(([p, c]) => upsert(`meta[property="${p}"]`, "meta", { property: p, content: c }));
  Object.entries({ "twitter:title": head.title, "twitter:description": head.description, "twitter:image": head.og.image })
    .forEach(([n, c]) => upsert(`meta[name="${n}"]`, "meta", { name: n, content: c }));

  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  head.alternates.forEach((a) => {
    const link = document.createElement("link");
    link.rel = "alternate"; link.hreflang = a.hreflang; link.href = a.href;
    document.head.appendChild(link);
  });

  const ld = upsert("script#ld-json", "script", { id: "ld-json", type: "application/ld+json" });
  ld.text = JSON.stringify(head.jsonLd);
};

export const Layout = ({ lang, pageKey, children }) => {
  const t = translations[lang];

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    applyHead(buildHead({ siteConfig, translations, pageSlugs, langs: LANGS, defaultLang: DEFAULT_LANG, lang, pageKey }));
  }, [lang, pageKey]);

  return (
    <LanguageContext.Provider value={{ lang, pageKey, t }}>
      <div className="App min-h-screen bg-[var(--bg-primary)]">
        <Header />
        <main>{children}</main>
        <Footer />
        <WhatsAppButton />
        <MobileContactBar />
      </div>
    </LanguageContext.Provider>
  );
};
